import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { testResultAPI, proxyAPI } from '../services/api';

function DraftResults() {
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter metadata
  const [projects, setProjects] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filter state (stage is always 'draft')
  const [filters, setFilters] = useState({
    stage: 'draft',
    testCaseName: '',
    project: '',
    status: '',
    proxyId: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    fetchFilterMetadata();
    fetchProxies();
  }, []);

  useEffect(() => {
    fetchResults();
  }, [filters]);

  const fetchFilterMetadata = async () => {
    try {
      const response = await testResultAPI.getFilterMetadata();
      setProjects(response.data.projects);
    } catch (err) {
      console.error('Failed to fetch filter metadata:', err);
    }
  };

  const fetchProxies = async () => {
    try {
      const response = await proxyAPI.getAll();
      setProxies(response.data);
    } catch (err) {
      console.error('Failed to fetch proxies:', err);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await testResultAPI.getAll(filters);
      setResults(response.data.results);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      stage: 'draft',
      testCaseName: '',
      project: '',
      status: '',
      proxyId: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
      page: 1,
      limit: 20
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this result?')) {
      return;
    }

    try {
      await testResultAPI.delete(id);
      fetchResults();
    } catch (err) {
      alert('Failed to delete result: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      running: 'badge-warning',
      failed: 'badge-danger'
    };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>Draft Results</h2>
          <Link to="/" className="btn btn-secondary">
            Back to Test Cases
          </Link>
        </div>

        <p className="text-muted mb-3">
          All test results start here. Promote them to Main stage for tracking or to Performance for business metrics.
        </p>

        {/* Filters Toggle */}
        <div className="flex-between mb-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-sm btn-secondary"
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h3 style={{ marginTop: 0 }}>Filters</h3>

          <div className="grid grid-3 mb-3">
            <div className="form-group">
              <label>Test Case Name</label>
              <input
                type="text"
                value={filters.testCaseName}
                onChange={(e) => handleFilterChange('testCaseName', e.target.value)}
                placeholder="Search by name..."
              />
            </div>

            <div className="form-group">
              <label>Project</label>
              <select
                value={filters.project}
                onChange={(e) => handleFilterChange('project', e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="running">Running</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-3 mb-3">
            <div className="form-group">
              <label>Proxy</label>
              <select
                value={filters.proxyId}
                onChange={(e) => handleFilterChange('proxyId', e.target.value)}
              >
                <option value="">All Proxies</option>
                <option value="none">No Proxy</option>
                {proxies.map(proxy => (
                  <option key={proxy._id} value={proxy._id}>{proxy.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Date From</label>
              <input
                type="datetime-local"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Date To</label>
              <input
                type="datetime-local"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-3 mb-3">
            <div className="form-group">
              <label>Sort By</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="createdAt">Date Created</option>
                <option value="testCaseName">Test Case Name</option>
                <option value="project">Project</option>
                <option value="status">Status</option>
              </select>
            </div>

            <div className="form-group">
              <label>Sort Order</label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div className="form-group">
              <label>Results Per Page</label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <div className="flex">
            <button onClick={clearFilters} className="btn btn-secondary">
              Clear Filters
            </button>
          </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center mt-3">
            <p>No draft results found. Run some tests to get started!</p>
          </div>
        ) : (
          <>
            {/* Results Table */}
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Test Case</th>
                    <th>Project</th>
                    <th>Proxy</th>
                    <th>Status</th>
                    <th>Total Requests</th>
                    <th>Success Rate</th>
                    <th>Avg Latency</th>
                    <th>Concurrency</th>
                    <th>Executed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(result => (
                    <tr key={result._id}>
                      <td>
                        <Link to={`/result/${result._id}`}>
                          {result.testCaseName}
                        </Link>
                      </td>
                      <td>{result.project}</td>
                      <td>
                        {result.proxyId ? (
                          <Link to={`/proxies/${result.proxyId}`}>
                            {result.proxyName}
                          </Link>
                        ) : result.proxyListId ? (
                          <Link to={`/proxy-lists/${result.proxyListId}`}>
                            {result.proxyListName}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span className={getStatusBadge(result.status)}>
                          {result.status}
                        </span>
                      </td>
                      <td>{result.metrics?.total_requests || 0}</td>
                      <td>
                        <span className={result.metrics?.success_rate >= 95 ? 'badge badge-success' : 'badge badge-warning'}>
                          {result.metrics?.success_rate || 0}%
                        </span>
                      </td>
                      <td>{result.metrics?.latency_metrics?.avg?.toFixed(2) || 0}ms</td>
                      <td>{result.executionConfig?.concurrency || 'N/A'}</td>
                      <td>{new Date(result.createdAt).toLocaleString()}</td>
                      <td>
                        <div className="btn-group">
                          <Link
                            to={`/result/${result._id}`}
                            className="btn btn-sm btn-primary"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleDelete(result._id)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex-between mt-3">
                <p className="text-muted">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                </p>
                <div className="flex" style={{ gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="btn btn-sm btn-secondary"
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0.5rem 1rem' }}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="btn btn-sm btn-secondary"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DraftResults;
